import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { LockSimple } from '@phosphor-icons/react';

import Button from 'partial/components/Button';
import Checkbox from 'partial/components/Checkbox';
import EmptyState from 'partial/components/EmptyState';
import { isEmpty } from 'lodash';
import { aclHelper } from 'helper';

import {
  useAllPermissions,
  usePermissionsById,
  useUpdatePermission,
} from '../hooks';

const permissionsTitle = ['read', 'write', 'update', 'delete', 'execute'];
const permissionsArr = [
  { id: 0, permission: 'r' },
  { id: 1, permission: 'w' },
  { id: 2, permission: 'u' },
  { id: 3, permission: 'd' },
  { id: 4, permission: 'x' },
];

/* Cells and header share one grid so the columns can never drift apart. */
const GRID = 'grid grid-cols-[minmax(180px,1fr)_repeat(5,88px)] items-center';
const SKELETON = ['s1', 's2', 's3', 's4', 's5'];

const Permissions = ({ roleId }) => {
  const [isAllPermissionsLoading, allPermissions] = useAllPermissions();
  const [isPermissionsByIdLoading, permissionsById] =
    usePermissionsById(roleId);
  const [isAdd, setAdd] = useState(false);
  const [pending, setPending] = useState(false);

  const isLoading = useMemo(
    () => isAllPermissionsLoading || isPermissionsByIdLoading,
    [isPermissionsByIdLoading, isAllPermissionsLoading],
  );

  const mapPermissions = useMemo(
    () =>
      allPermissions?.map((item) => ({
        ...item,
        access:
          permissionsById?.find((x) => x?.name === item?.name)?.access || [],
      })),
    [allPermissions, permissionsById],
  );

  const [newPermissions, setNewPermissions] = useState(mapPermissions);

  const getPermissions = useMemo(
    () => (isAdd ? newPermissions : mapPermissions),
    [mapPermissions, newPermissions, isAdd],
  );

  const onError = () => setPending(false);
  const onSuccess = () => {
    setAdd(false);
    setPending(false);
  };

  const [isUpdateLoading, update] = useUpdatePermission(onSuccess, onError);

  /* Keep `access` in the canonical r-w-u-d-x order without mutating Array. */
  const setAccess = (index, nextAccess) =>
    setNewPermissions((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        access: permissionsArr
          .map((p) => p.permission)
          .filter((p) => nextAccess.includes(p)),
      };
      return next;
    });

  const handleCheckbox = (item, permission, index) => {
    const access = item?.access || [];
    setAccess(
      index,
      access.includes(permission)
        ? access.filter((p) => p !== permission)
        : [...access, permission],
    );
  };

  /* Row axis: all five operations on one resource. */
  const rowState = (item) => {
    const available = (item?.available_access || []).length;
    const granted = (item?.access || []).length;
    return {
      checked: available > 0 && granted === available,
      indeterminate: granted > 0 && granted < available,
      available,
    };
  };

  const toggleRow = (item, index) => {
    const { checked } = rowState(item);
    setAccess(index, checked ? [] : [...(item?.available_access || [])]);
  };

  /* Column axis: one operation across every resource. */
  const columnState = useCallback(
    (permission) => {
      const rows = (getPermissions || []).filter((r) =>
        r?.available_access?.includes(permission),
      );
      const granted = rows.filter((r) =>
        r?.access?.includes(permission),
      ).length;
      return {
        checked: rows.length > 0 && granted === rows.length,
        indeterminate: granted > 0 && granted < rows.length,
      };
    },
    [getPermissions],
  );

  const toggleColumn = (permission) => {
    const { checked } = columnState(permission);
    setNewPermissions((prev) =>
      prev.map((row) => {
        if (!row?.available_access?.includes(permission)) return row;
        const access = row?.access || [];
        const next = checked
          ? access.filter((p) => p !== permission)
          : [...new Set([...access, permission])];
        return {
          ...row,
          access: permissionsArr
            .map((p) => p.permission)
            .filter((p) => next.includes(p)),
        };
      }),
    );
  };

  /* Summary counts are derived from the cells — never hard-coded. */
  const summary = useMemo(() => {
    const rows = getPermissions || [];
    const cells = rows.reduce(
      (n, r) => n + (r?.available_access?.length || 0),
      0,
    );
    const granted = rows.reduce((n, r) => n + (r?.access?.length || 0), 0);
    return { resources: rows.length, cells, granted };
  }, [getPermissions]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      update({
        permissions: newPermissions
          ?.map((item) => ({ id: item?.id, access: item?.access }))
          .filter((item) => !isEmpty(item?.access)),
        id: roleId,
      });
    },
    [newPermissions, roleId, update],
  );

  /*
   * `mapPermissions` is rebuilt from the two query results on every render, so
   * depending on its identity re-ran this effect forever (setState -> render ->
   * new array -> setState). Key the reset on the CONTENT instead.
   */
  const permissionsKey = useMemo(
    () =>
      JSON.stringify(
        (mapPermissions || []).map((x) => [x?.id, (x?.access || []).join('')]),
      ),
    [mapPermissions],
  );

  useEffect(() => {
    if (!mapPermissions.length) return;
    setNewPermissions(mapPermissions);
    setAdd(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsKey, roleId]);

  if (isLoading) {
    return (
      <div className="p-6 space-y-2">
        {SKELETON.map((k) => (
          <div
            key={k}
            className="animate-pulse bg-neutral-100 h-row w-full rounded"
          />
        ))}
      </div>
    );
  }

  if (!mapPermissions.length) {
    return (
      <EmptyState
        statement="No permissions are defined."
        guidance="Resources appear here once the access-control list is populated."
      />
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4 gap-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[.05em] text-neutral-500">
            Permissions
          </h3>
          <p className="mt-1 text-sm text-neutral-500 tabular-nums">
            {summary.granted} of {summary.cells} granted across{' '}
            {summary.resources} resources
          </p>
        </div>
        {isAdd ? (
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Button secondary onClick={() => setAdd(false)}>
              Discard changes
            </Button>
            <Button
              primary
              type="submit"
              isLoading={pending || isUpdateLoading}
              loadingLabel="Saving…"
            >
              Save permissions
            </Button>
          </form>
        ) : (
          aclHelper(
            'role_permission',
            'u',
            <Button primary onClick={() => setAdd(true)}>
              Edit permissions
            </Button>,
          )
        )}
      </div>

      {/*
        Sticky header lives INSIDE the scrolled content, not the viewport shell —
        that is what makes it work inside react-custom-scrollbars-2.
      */}
      <div className="border border-neutral-200 rounded-md overflow-auto max-h-[60vh]">
        <div
          className={cn(
            GRID,
            'sticky top-0 z-10 h-thead bg-neutral-100 border-b border-neutral-300',
          )}
        >
          <div className="px-4 text-xs font-semibold uppercase tracking-[.05em] text-neutral-500">
            Resource
          </div>
          {permissionsArr.map((x, i) => {
            const state = columnState(x.permission);
            return (
              <div
                key={x.id}
                className="flex flex-col items-center justify-center gap-1"
              >
                <span className="text-xs font-semibold uppercase tracking-[.05em] text-neutral-500">
                  {permissionsTitle[i]}
                </span>
                {isAdd && (
                  <Checkbox
                    checked={state.checked}
                    indeterminate={state.indeterminate}
                    onChange={() => toggleColumn(x.permission)}
                    label={`Toggle ${permissionsTitle[i]} on every resource`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {(getPermissions || []).map((item, index) => {
          const row = rowState(item);
          const locked = row.available === 0;
          return (
            <div
              key={item?.id}
              className={cn(
                GRID,
                'h-row border-b border-neutral-100 last:border-b-0',
                // eslint-disable-next-line no-nested-ternary
                locked
                  ? 'bg-neutral-50'
                  : index % 2 === 1
                  ? 'bg-neutral-50'
                  : 'bg-white',
              )}
            >
              <div className="px-4 flex items-center gap-2.5 min-w-0">
                {isAdd && !locked && (
                  <Checkbox
                    checked={row.checked}
                    indeterminate={row.indeterminate}
                    onChange={() => toggleRow(item, index)}
                    label={`Toggle every operation on ${item?.name}`}
                  />
                )}
                <span
                  className={cn(
                    'text-sm font-medium capitalize truncate',
                    locked ? 'text-neutral-400' : 'text-neutral-900',
                  )}
                >
                  {item?.name?.replace(/_/g, ' ')}
                </span>
                {locked && (
                  <LockSimple
                    size={14}
                    className="text-neutral-400 flex-shrink-0"
                  />
                )}
              </div>

              {permissionsArr.map((x) => {
                const available = item?.available_access?.includes(
                  x?.permission,
                );
                const granted = item?.access?.includes(x?.permission);
                return (
                  <div key={x?.id} className="flex justify-center">
                    <Checkbox
                      checked={Boolean(granted)}
                      disabled={!isAdd || !available}
                      denied={!available}
                      onChange={() =>
                        handleCheckbox(item, x?.permission, index)
                      }
                      label={`${permissionsTitle[x.id]} ${item?.name}`}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

Permissions.propTypes = {
  roleId: PropTypes.string.isRequired,
};

export default memo(Permissions);
